import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Paper, TableCell } from '@material-ui/core';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import Table from '@material-ui/core/Table';
import { Check } from '@mui/icons-material';
import { Cancel, StarSharp } from '@material-ui/icons';
import classNames from 'classnames';
import UpgradePageForm from './UpgradePageForm';
import {
  BACKGROUND_DARK, CUSTOM_BOX_SHADOW, DARK_RED_BOX, DASHBOARD_BOX_SHADOW_DARK,
} from '../../../const/color-const';
import { darken } from '@material-ui/core/styles';
import { HOME_URL } from '../../../const/routes';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  table: {
    width: '100%',
    maxWidth: '800px',
    margin: theme.spacing(2, 2, 0, 2),
    boxShadow: theme.palette.type === 'light' ? CUSTOM_BOX_SHADOW : DASHBOARD_BOX_SHADOW_DARK,
  },
  tableHeader: {
    height: 58,
    fontWeight: 'bold',
    backgroundColor: theme.palette.type === 'light' ? '#232836' : darken(BACKGROUND_DARK, 0.4),
    '& th': {
      padding: 0,
      fontSize: 16,
      color: 'white',
    },
  },
  tableRow: {
    '& td': {
      height: 52,
      fontWeight: '500',
    },
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.type === 'light' ? 'white' : darken(BACKGROUND_DARK, 0.1),
    },
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.type === 'light' ? '#f2f2f2' : darken(BACKGROUND_DARK, 0.25),
    },
    '& td:nth-child(2), & td:last-child': {
      textAlign: 'center',
    },
  },
  pageHeader: {
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  textBox: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: theme.spacing(3),
    '& p': {
      padding: theme.spacing(0.5),
    },
  },
  upgradeContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  textParagraph: {
    textAlign: 'center',
    maxWidth: '65%',
    fontSize: 16,
    padding: 0,
    margin: 0,
  },
  textCta: {
    fontWeight: 'bold',
    padding: theme.spacing(2, 1, 1, 1),
  },
  tableHeaderCell: {
    fontWeight: 'bold',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    '&:first-child': {
      width: '60%',
      maxWidth: '100%',
      padding: theme.spacing(1),
    },
    '&:nth-child(2), &:last-child': {
      width: '20%',
      maxWidth: '100%',
      textAlign: 'center',
    },
  },
  starIcon: { color: 'gold' },
  iconStylePro: { color: 'green' },
  iconStyleBasic: { color: DARK_RED_BOX, fontSize: 20 },
  featureHeader: { marginLeft: theme.spacing(2) },
}));

const UpgradePage = React.forwardRef(({ user }, ref) => {
  const classes = useStyles();
  const history = useHistory();
  const IconPro = <Check className={classes.iconStylePro} />;
  const IconBasic = <Cancel className={classes.iconStyleBasic} />;
  const Version = user?.LicenseInfo?.Version;
  const { formatMessage } = useIntl();
  const daysBackBoldText = formatMessage({ id: 'page.upgrade.paragraph3.days', defaultMessage: '10 days back' });
  
  useEffect(() => {
    if (Version === 'Pro') {
      history.push(HOME_URL);
    }
  }, [Version, history]);
  
  const renderFunctionalityCell = (text) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <StarSharp className={classes.starIcon} /> <p className={classes.featureHeader}>{text}</p>
      </div>
    );
  };
  
  const Bold = ({ children }) => <b>{children}</b>;
  
  const tableRows = [
    [renderFunctionalityCell(<FormattedMessage id="page.upgrade.features"
                                               defaultMessage="Features" />), 'BAS', 'PRO'],
    [<FormattedMessage id="page.upgrade.list0" defaultMessage="Access to dashboard" />, IconPro, IconPro],
    [<FormattedMessage id="page.upgrade.list9" defaultMessage="Access to company invoices" />, IconPro, IconPro],
    [<FormattedMessage id="page.upgrade.list1"
                       defaultMessage="Sales reports: top list per product, top list per category, sales per day, sales comparisons, etc." />, IconBasic, IconPro],
    [<FormattedMessage id="page.upgrade.list2" defaultMessage="Copies of Z-reports" />, IconBasic, IconPro],
    [<FormattedMessage id="page.upgrade.list3"
                       defaultMessage="Periodic reports for any date period" />, IconBasic, IconPro],
    [<FormattedMessage id="page.upgrade.list4"
                       defaultMessage="SIE4 files (+ integration with external accounting system)" />, IconBasic, IconPro],
    [<FormattedMessage id="page.upgrade.list5" defaultMessage="Bookkeeping accounting" />, IconBasic, IconPro],
    [<FormattedMessage id="page.upgrade.list6"
                       defaultMessage="Third-party integrations" />, IconBasic, IconPro],
    [<FormattedMessage id="page.upgrade.list7"
                       defaultMessage="Transaction details for 2 years back in time" />, IconBasic, IconPro],
  ];
  
  return (
    <Box className={classes.upgradeContent}>
      <h1 className={classes.pageHeader}>
        <FormattedMessage id="page.upgrade.pro" defaultMessage="Upgrade to PRO" />
      </h1>
  
      <div className={classes.textBox}>
        <p className={classes.textParagraph}>
          <FormattedMessage id="page.upgrade.paragraph2"
                            defaultMessage='Your account is currently of the "{type}" type.'
                            values={{ type: <Bold>BAS</Bold> }}
          />
        </p>
    
        <p className={classes.textParagraph}>
          <FormattedMessage id="page.upgrade.paragraph3"
                            defaultMessage="A BAS account gives you a general overview of your business and the ability to view transactions {days}"
                            values={{ days: <Bold>{daysBackBoldText}.</Bold> }}
          />
        </p>
      </div>
      
      <p className={classNames(classes.textParagraph, classes.textCta)}>
        <FormattedMessage id="page.upgrade.paragraph4"
                          defaultMessage="With an upgrade to Dinkassa.se Pro you get access to, among other things:" />
      </p>
      
      <TableContainer component={Paper} className={classes.table}>
        <Table size="small">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              {tableRows[0].map((headerCell, index) => (
                <TableCell key={index} className={classes.tableHeaderCell}>
                  {headerCell}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.slice(1).map((row, rowIndex) => (
              <TableRow key={rowIndex} className={classes.tableRow}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex} className={classes.tableCell}>
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
  
      <div className={classes.textBox}>
        <p className={classes.textParagraph}>
          <FormattedMessage id="page.upgrade.paragraph5"
                            defaultMessage="{pro} comes with an additional fee based on the number of cash registers."
                            values={{ pro: <Bold>Dinkassa.se Pro</Bold> }}
          />
        </p>
    
        <br />
    
        <p className={classes.textParagraph}>
          <FormattedMessage id="page.upgrade.paragraph6"
                            defaultMessage="Register your interest for an upgrade to {dinkassa} and we will get back to you at the email address registered to this account."
                            values={{ dinkassa: <Bold>Dinkassa.se Pro</Bold> }}
          />
        </p>
      </div>
  
  
      <UpgradePageForm />
    </Box>
  );
});

UpgradePage.propTypes = {
  user: PropTypes.object,
};

export default compose(
  connect(store => ({
    user: store.user.profile,
  })),
)(UpgradePage);
